/**
 * Ключи провайдера (C7).
 *
 * Значения не приходят с сервера — только маска вида `sk-••••cdef`. Поэтому
 * поле пустое означает «не менять», а не «очистить»: очистка делается явной
 * кнопкой. Иначе открыв форму и нажав «Сохранить», админ стёр бы все ключи.
 */
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AdminProviderCheck, AdminProviderRow } from '@alterium/shared';
import { adminApi } from '../../api/admin';
import { Alert } from '../ui/Alert';
import { Button } from '../ui/Button';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../i18n/errors';

/** Поля ключей — данные: держим ключи перевода, текст подставляет компонент. */
const FIELDS: Array<{ key: string; labelKey: string; hintKey?: string }> = [
  { key: 'apiKey', labelKey: 'admin.creds.apiKey' },
  { key: 'apiSecret', labelKey: 'admin.creds.apiSecret', hintKey: 'admin.creds.apiSecretHint' },
  { key: 'webhookSecret', labelKey: 'admin.creds.webhookSecret', hintKey: 'admin.creds.webhookHint' },
  { key: 'baseUrl', labelKey: 'admin.creds.baseUrl', hintKey: 'admin.creds.baseUrlHint' },
];

interface ProviderCredentialsProps {
  provider: AdminProviderRow;
  onSaved: () => void;
}

export function ProviderCredentials({ provider, onSaved }: ProviderCredentialsProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, string>>({});
  const [check, setCheck] = useState<AdminProviderCheck | null>(null);

  const save = useMutation({
    mutationFn: (input: Record<string, string>) => adminApi.updateCredentials(provider.id, input),
    onSuccess: () => {
      setValues({});
      setCheck(null);
      onSaved();
    },
  });

  const verify = useMutation({
    mutationFn: () => adminApi.checkProvider(provider.id),
    onSuccess: (result) => {
      setCheck(result);
      onSaved();
    },
  });

  // Отправляем только заполненные поля: пустое — «не трогать».
  const filled = Object.fromEntries(
    Object.entries(values).filter(([, value]) => value.trim().length > 0),
  );

  return (
    <div className="flex flex-col gap-3 border-t border-line bg-surface-secondary px-3 py-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-meta text-content-secondary">{t('admin.provider.keys')}</span>
        {provider.configured ? (
          <span className="text-meta text-success">{t('admin.creds.configured')}</span>
        ) : (
          <span className="text-meta text-warning">{t('admin.creds.missing')}</span>
        )}
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {FIELDS.map((field) => {
          const current = provider.credentials[field.key];

          return (
            <label key={field.key} className="flex flex-col gap-1">
              <span className="text-meta text-content-muted">
                {t(field.labelKey)}
                {field.hintKey ? ` · ${t(field.hintKey)}` : ''}
              </span>
              <input
                type={field.key === 'baseUrl' ? 'text' : 'password'}
                value={values[field.key] ?? ''}
                onChange={(event) =>
                  setValues((state) => ({ ...state, [field.key]: event.target.value }))
                }
                placeholder={current ?? t('admin.creds.notSet')}
                autoComplete="off"
                className="h-8 rounded-control border border-line bg-surface px-2 text-meta text-content placeholder:text-content-muted focus:border-accent focus:outline-none"
              />
              {current ? (
                <button
                  type="button"
                  onClick={() => save.mutate({ [field.key]: '' })}
                  className="w-fit text-[11px] text-content-muted underline transition-colors duration-fast hover:text-error"
                >{t('admin.creds.clear')}</button>
              ) : null}
            </label>
          );
        })}
      </div>

      {save.isError ? (
        <Alert tone="error">{errorText(save.error)}</Alert>
      ) : null}

      {check ? (
        <Alert tone={check.ok ? 'success' : 'error'}>{check.message}</Alert>
      ) : null}

      <div className="flex gap-2">
        <Button
          variant="secondary"
          disabled={save.isPending || Object.keys(filled).length === 0}
          onClick={() => save.mutate(filled)}
        >
          {save.isPending ? t('common.saving') : t('common.save')}
        </Button>
        <Button variant="tertiary" disabled={verify.isPending} onClick={() => verify.mutate()}>
          {verify.isPending ? t('admin.creds.checking') : t('admin.creds.check')}
        </Button>
      </div>
    </div>
  );
}
